import { useEffect, useRef, useState } from 'react';
import Slider from '@mui/material/Slider';
import { useTranslation } from 'react-i18next';
import LangSelector from './LangSelector';

const themee = () => localStorage.getItem('theme') || 'dark';
type ChangedField = "starting" | "hours" | "ending";

export default function App() {
  const { t } = useTranslation();
  const [hours, setHours] = useState(9.5);
  //const [party, setParty] = useState(false);
  const [startingMinute, setStartingMinute] = useState(1350);
  const [endingMinute, setEndingMinute] = useState(540);
  const [themeInput, setThemeInput] = useState(themee());
  const [lastChanged, setLastChanged] = useState<ChangedField>("hours");

  const adContainerRef = useRef<HTMLDivElement>(null);

  function timeToMinutes(time: string): number {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  }

  function minutesToTime(totalMinutes: number): string {
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(h)}:${pad(m)}`;
  }

  // These helper functions shift slider values so that 0 corresponds to noon.
  function actualToSliderTime(actual: number): number {
    return (actual - 720 + 1440) % 1440;
  }

  function sliderTimeToActual(sliderValue: number): number {
    return (sliderValue + 720) % 1440;
  }

  useEffect(() => {
    localStorage.setItem('theme', themeInput);
  }, [themeInput]);

  function themeSetter() {
    if (themeInput === 'dark') {setThemeInput('light')} else {setThemeInput('dark')};
  }

  // Update logic:
  // - When the hours field is changed: update the going-to-sleep time.
  // - When either the going-to-sleep (starting) or wake-up (ending) time changes: recalc the hours.
  useEffect(() => {
    if (lastChanged === "hours") {
      const totalSleepMinutes = Math.round(hours * 60);
      const autoCalculatedStarting = (endingMinute - totalSleepMinutes + 1440) % 1440;
      if (autoCalculatedStarting !== startingMinute) {
        setStartingMinute(autoCalculatedStarting);
      }
    } else if (lastChanged === "starting" || lastChanged === "ending") {
      const minutesDiff = (endingMinute - startingMinute + 1440) % 1440;
      const newHours = minutesDiff / 60;
      if (newHours !== hours) {
        setHours(newHours);
      }
    }
  }, [lastChanged, hours, endingMinute, startingMinute]);

  // Handle dual-slider change.
  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      const newStart = sliderTimeToActual(newValue[0]);
      const newEnd = sliderTimeToActual(newValue[1]);
      // Determine which handle the user changed.
      if (newStart !== startingMinute) {
        setStartingMinute(newStart);
        setLastChanged("starting");
      } else if (newEnd !== endingMinute) {
        setEndingMinute(newEnd);
        setLastChanged("ending");
      }
    }
  };

  function funkyThing(): string {
    const d = new Date();
    const currentMinutes = d.getHours() * 60 + d.getMinutes();
    let inTimeRange = false;
    // If the range does not cross midnight.
    if (startingMinute <= endingMinute) {
        inTimeRange = currentMinutes >= startingMinute && currentMinutes <= endingMinute;
    } else {
        // If the range crosses midnight.
        inTimeRange = currentMinutes >= startingMinute || currentMinutes <= endingMinute;
    }
    if (inTimeRange) {
        return t("wait");
    }
    return "";
  }

  useEffect(() => {
    const container = adContainerRef.current;
    if (container) {
      // Clear any existing content.
      container.innerHTML = '';

      // Create first script tag.
      const script1 = document.createElement('script');
      script1.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6131263490509982";
      script1.async = true;
      script1.crossOrigin = 'anonymous';
      container.appendChild(script1);

      // Create ins tag.
      const ins = document.createElement('ins');
      ins.className = "adsbygoogle block w-full h-full";
      ins.setAttribute("data-ad-client", "ca-pub-6131263490509982");
      ins.setAttribute("data-ad-slot", "2240810058");
      ins.setAttribute("data-ad-format", "auto");
      ins.setAttribute("data-full-width-responsive", "true");
      container.appendChild(ins);

      // Create second script tag (inline).
      const script2 = document.createElement('script');
      script2.text = "(adsbygoogle = window.adsbygoogle || []).push({});";
      container.appendChild(script2);
    }
  }, []);

  // Calculate slider values from state. The slider uses a noon-to-noon scale.
  const sliderValue = [actualToSliderTime(startingMinute), actualToSliderTime(endingMinute)];

  return (
    <main className={`${themeInput} transition-colors duration-300 dark:bg-black dark:text-white bg-white text-black min-w-screen min-h-screen p-4`}>
      <h1 className='md:text-4xl text-2xl font-bold mb-8'>{t('title')}</h1>
      <div className='flex flex-col justify-center items-center min-h-[calc(((100vh-2rem)-5rem)-3.5rem)] text-center'>
        <div className='min-w flex flex-col xl:flex-row items-center xl:gap-10 gap-6'>
          <div className='flex justify-center items-center xl:py-8'><h1 className='xl:text-3xl text-xl font-bold'>{t("if")}</h1></div>
          <div className='flex flex-col items-center gap-4'>
            <div className={`flex items-center gap-4 ${/*party ? "text-gray-500 pointer-events-none" : ""*/""}`}>
              <input className='text-2xl w-26 rounded-xl outline-2 outline-offset-2 outline-indigo-500 p-2' disabled={/*party*/false} type="number" min="0" max="23.99" value={hours}
                onChange={(e) => {
                  setHours(Number(e.currentTarget.value));
                  setLastChanged("hours");
              }} />
              <span className='text-lg'>{t("hours")}</span>
            </div>
            {/*<div className='flex items-center gap-4'>
              <input type="checkbox" checked={party} onChange={(e) => {setParty(e.currentTarget.checked)}} />
              <span>{t("party")}</span>
            </div>*/}
          </div>
          <div className='flex justify-center items-center xl:py-8'><h1 className='xl:text-3xl text-xl font-bold'>{t("and")}</h1></div>
          <div className='flex flex-col items-center gap-4'>
            <div className="flex items-center gap-4 text-2xl">
              <input className='rounded-xl outline-2 outline-offset-2 outline-indigo-500 p-2' type="time" value={minutesToTime(endingMinute)} 
                onChange={(e) => {
                  setEndingMinute(timeToMinutes(e.currentTarget.value));
                  setLastChanged("ending");
              }} />
            </div>
          </div>
          <div className='flex justify-center items-center xl:py-8'><h1 className='xl:text-3xl text-xl font-bold'>{t("so")}</h1></div>
          <div className='flex flex-col items-center gap-4'>
            <div className="flex items-center gap-4 text-2xl">
              <input className='rounded-xl outline-2 outline-offset-2 outline-indigo-500 p-2' type="time" value={minutesToTime(startingMinute)}
                onChange={(e) => {
                  setStartingMinute(timeToMinutes(e.currentTarget.value));
                  setLastChanged("starting");
              }} />
            </div>
          </div>
        </div>
        {/* Dual-handle slider with noon-to-noon scale */}
        <div className="w-5/6 mt-8">
          <Slider
            min={0}
            max={1439}
            step={15}
            value={sliderValue}
            onChange={handleSliderChange}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => minutesToTime(sliderTimeToActual(value))}
          />
        </div>
        <div className='w-full h-full flex-grow flex flex-col items-center md:justify-center gap-6'>
          <hr className='md:hidden block h-1 w-full' />
          <h1 className='sm:text-4xl text-xl font-bold'>{funkyThing()}</h1>
          { /* ads */ }
          <div className='relative w-full lg:h-[280px]'>
            <div className='absolute w-full h-full' ref={adContainerRef}></div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-4 right-4 flex gap-4 z-10">
        { /* Media theme button */ }
        <button className="bg-[#000]/50 backdrop-blur-lg rounded-lg text-white p-4 w-14 h-14 outline-2 outline-offset-2 outline-indigo-500" onClick={themeSetter}>
          <span className="material-symbols-outlined">{themeInput === "dark" ? "light_mode" : "moon_stars"}</span>
        </button>
      </div>
      <LangSelector />
      <div className="absolute w-[calc(100%-2rem)] flex justify-center items-center bottom-4 dark:xl:text-white dark:text-black left-4 mt-4 mr-4 text-center">
          {t("copyright")}<br/>{t("disclaimer")}
      </div>
    </main>
  )
};
